<template>
  <div class="heat-work-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$W + Q = ΔU | W = J·Q$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
           <defs>
              <linearGradient id="water-grad" x1="0" y1="0" x2="0" y2="1">
                 <stop offset="0%" stop-color="rgba(56, 189, 248, 0.4)" />
                 <stop offset="100%" stop-color="rgba(56, 189, 248, 0.1)" />
              </linearGradient>
           </defs>

           <!-- 背景网格 -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 13" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="400" />
              <line v-for="i in 9" :key="'h'+i" x1="0" :y1="i*50" x2="600" :y2="i*50" />
           </g>

           <!-- 实验装置支架 -->
           <line x1="120" y1="50" x2="120" y2="350" stroke="#475569" stroke-width="6" stroke-linecap="round" />
           <line x1="80" y1="350" x2="160" y2="350" stroke="#475569" stroke-width="8" stroke-linecap="round" />
           <line x1="120" y1="80" x2="280" y2="80" stroke="#475569" stroke-width="4" />
           
           <!-- 定滑轮 -->
           <circle cx="120" cy="80" r="15" fill="#1e293b" stroke="#cbd5e1" stroke-width="3" />
           <circle cx="280" cy="80" r="15" fill="#1e293b" stroke="#cbd5e1" stroke-width="3" />

           <!-- 绳子与重物 (随 weightPos 改变高度) -->
           <line x1="105" y1="80" x2="105" :y2="100 + weightPos" stroke="#94a3b8" stroke-width="2" stroke-dasharray="4,2" />
           <line x1="120" y1="65" x2="280" y2="65" stroke="#94a3b8" stroke-width="2" />
           <line x1="295" y1="80" x2="295" y2="150" stroke="#94a3b8" stroke-width="2" />

           <g :transform="`translate(105, ${100 + weightPos})`">
              <rect x="-25" y="0" width="50" height="40" fill="#facc15" stroke="#ca8a04" stroke-width="3" rx="4" />
              <text x="0" y="25" text-anchor="middle" fill="#713f12" font-size="16" font-weight="900">M</text>
           </g>

           <!-- 隔热量热器 (绝热容器) -->
           <rect x="220" y="150" width="150" height="150" fill="rgba(15, 23, 42, 0.6)" stroke="#64748b" stroke-width="8" rx="8" stroke-linejoin="round" />
           
           <!-- 水体 -->
           <rect x="225" y="160" width="140" height="135" fill="url(#water-grad)" rx="4" />

           <!-- 中心主轴与搅拌桨叶 -->
           <g transform="translate(295, 225)">
              <line x1="0" y1="-75" x2="0" y2="50" stroke="#cbd5e1" stroke-width="6" />
              <!-- 通过改变角度展现旋转 -->
              <rect x="-40" y="-5" width="80" height="10" fill="#475569" rx="3" :transform="`scale(${Math.cos(paddleAngle)})`" />
              <rect x="-40" y="25" width="80" height="10" fill="#475569" rx="3" :transform="`scale(${Math.sin(paddleAngle)})`" />
           </g>

           <!-- 温度计模拟 -->
           <g transform="translate(420, 100)">
              <!-- 玻管 -->
              <rect x="-5" y="0" width="10" height="150" fill="rgba(255,255,255,0.9)" rx="5" />
              <circle cx="0" cy="150" r="12" fill="#ef4444" />
              <!-- 液柱 (根据当前温度变高) -->
              <!-- 20C对应高20px, 21C对应高120px，夸张化方便观察 -->
              <rect x="-2" :y="150 - (currentTemp - 20) * 80" width="4" :height="(currentTemp - 20) * 80" fill="#ef4444" rx="2" />
              
              <!-- 刻度线 -->
              <line x1="-15" y1="150" x2="-5" y2="150" stroke="#94a3b8" />
              <text x="-25" y="154" fill="#94a3b8" font-size="12">20°</text>
              <line x1="-15" y1="70" x2="-5" y2="70" stroke="#94a3b8" />
              <text x="-25" y="74" fill="#94a3b8" font-size="12">21°</text>
           </g>
        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">热力学代数解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">实验数据实时监测</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>重物下落高度 h</span><span class="hl">{{ (weightPos / 100).toFixed(2) }} m</span></div>
                  <div class="data-row"><span>重力势能减少 (做功 W)</span><span class="hl text-blue">{{ totalWork.toFixed(1) }} J</span></div>
                  <div class="data-row"><span>水体温度 T</span><span class="hl text-red">{{ currentTemp.toFixed(3) }} °C</span></div>
                  <div class="data-row"><span>水体内能增加 ΔU</span><span class="hl text-green">{{ (totalWork * 0.95).toFixed(1) }} J</span></div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>焦耳量热实验：</strong><br/><br/>这套装置演示了做功与热传递在改变物体内能上的等效性。重锤坠落带动叶片在绝热容器的水中搅拌，克服水的粘滞阻力做功，将所有的宏观机械能转化为了水分子微观无规则运动的动能（即内能升高、温度上升）。这正是热力学第一定律的雏形实验。</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item">
               <label>重物坠落控制</label>
               <el-button type="warning" @mousedown.native="startDrop" @mouseup.native="stopDrop" @mouseleave.native="stopDrop" style="width: 100%; font-weight:bold;">
                 <i class="el-icon-bottom"></i> {{ isDropping ? '松开暂停' : '长按释放重物做功' }}
               </el-button>
            </div>
            
            <div class="s-item">
               <label>实验重置</label>
               <el-button @click="reset" plain style="width: 100%;"><i class="el-icon-refresh-left"></i> 冷却水体并提升砝码</el-button>
            </div>

            <div class="s-item">
               <label>探究提示</label>
               <div style="font-size: 13px; color: #64748b; line-height: 1.4;">观察重锤下落、叶轮搅动与温度计液柱上升的同步关系。</div>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HeatWorkViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      weightPos: 0,
      totalWork: 0,
      currentTemp: 20.000,
      paddleAngle: 0,
      isDropping: false,
      running: true
    }
  },
  computed: {
    topicIntro() {
       return '内能及其改变：做功机制与物理热效应的能量当量转换';
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { this.running = false; },
  methods: {
    startDrop() { this.isDropping = true; },
    stopDrop() { this.isDropping = false; },
    reset() {
      this.weightPos = 0;
      this.totalWork = 0;
      this.currentTemp = 20.000;
      this.paddleAngle = 0;
    },
    animate() {
      if (!this.running) return;
      if (this.isDropping && this.weightPos < 150) {
          this.weightPos += 0.5;
          this.totalWork += 4.186; // Approx 1 cal
          // Q = cmΔt => dt = Q/cm
          this.currentTemp += 0.005;
          this.paddleAngle += 0.08;
      }
      requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.heat-work-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 16px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
